import { Grid } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BaseButton } from 'src/common/components/base-button';
import { ButtonRestrictionType } from 'src/common/enumerations/button-restriction-type';
import { calculateWidth } from 'src/common/helpers/dimension-helper';
import { ISidebarButton } from 'src/common/interfaces/dtos/ISidebarButton';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { useResponsiveContext } from 'src/home-page/hooks/responsive-context';
import EmeraldModal from '../../common/components/emerald-modal/emerald-modal';
import { styles } from './commands-styles';
import SidebarButton from './sidebar/sidebar-button';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';

export const Commands: React.FC<ICommandsProps> = ({
  visible,
  buttons,
  closeAndReset,
  handleGroupClick,
  isDisabled,
  trainingModeActive,
  buttonCount,
  isSubCategory,
  subGroupName,
}): JSX.Element => {
  const { showSidebar } = useResponsiveContext();
  const { t } = useTranslation();
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();

  const columns = Math.ceil(buttons.length / buttonCount);
  let width;
  if (!showSidebar) {
    width = columns * 155.03 + (columns - 1) * 10 + 30;
    width = calculateWidth(width, 1024).dimension;
  } else {
    width = (isSubCategory ? 2 : columns) * 11.25;
  }

  const handleButtonClick = (button: ISidebarButton) => {
    closeAndReset();
    button.handleClick();
  };

  return (
    <EmeraldModal open={visible} data-testid='commands-modal' onClose={closeAndReset}>
      <Grid
        container
        className={clsx(classes.container, {
          [trainingModeClasses.trainingCommandMenuModal]: trainingModeActive,
        })}
        justifyContent='space-between'
        direction='column'
        style={{ width: `${width}vw` }}
      >
        {trainingModeActive && (
          <TrainingModeWatermark supplementalStyles={trainingModeClasses.tradeMarkNoWarp} />
        )}
        <Grid item className={classes.header}>
          {t('Functions')}
        </Grid>
        <Grid
          item
          container
          direction='column'
          className={clsx(classes.actionContainer, {
            [trainingModeClasses.trainingModeBackground]: trainingModeActive,
          })}
        >
          {isSubCategory && <Grid className={classes.subGroupName}>{t(subGroupName || '')}</Grid>}
          {buttons.map(
            (button: ISidebarButton): JSX.Element => {
              return (
                <Grid
                  key={button.text}
                  className={
                    isSubCategory
                      ? classes.subCategory
                      : !showSidebar
                      ? classes.commandSmallForm
                      : classes.command
                  }
                >
                  <SidebarButton
                    text={button.text}
                    handleClick={() =>
                      button.buttonGroup
                        ? handleGroupClick(button.text, button.buttonGroup)
                        : handleButtonClick(button)
                    }
                    buttonIcon={button.buttonIcon}
                    isSelected={button.isSelected ?? false}
                    isDisabled={isDisabled(button.appliesTo) || button.isDisabled}
                    appliesTo={button.appliesTo}
                    buttonGroup={button.buttonGroup}
                    isSubCategory={isSubCategory}
                    showIcon={true}
                  />
                </Grid>
              );
            },
          )}
        </Grid>
        <Grid
          item
          className={clsx(classes.footer, {
            [trainingModeClasses.trainingModeBackground]: trainingModeActive,
          })}
        >
          <BaseButton data-testid='hide-command-modal' onClick={closeAndReset}>
            <span className={classes.hideButtonLabel}>
              {t(isSubCategory ? 'Cancel' : 'Hide Panel')}
            </span>
          </BaseButton>
        </Grid>
      </Grid>
    </EmeraldModal>
  );
};

export interface ICommandsProps {
  visible: boolean;
  buttons: ISidebarButton[];
  isDisabled: (buttonAppliedTo: ButtonRestrictionType[]) => boolean;
  closeAndReset: () => void;
  trainingModeActive: boolean;
  buttonCount: number;
  handleGroupClick: (groupName: string, buttonGroup: ISidebarButton[]) => void;
  isSubCategory: boolean;
  subGroupName?: string;
}
